import { useMemo } from 'react';
import { FormGroup, FormLabel } from 'react-bootstrap';
import ReactSelect, { StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useTheme } from 'styled-components';

interface SelectSearchableProps {
  id?: string;
  value?: string | number;
  label: string;
  disabled?: boolean;
  name: string;
  onChange?: (value: any) => void;
  onCreate?: (label: string) => void;
  isCreatable?: boolean;
  options: { value: any; label: string }[];
  style?: React.CSSProperties;
  required?: boolean;
  placeholder?: string;
  'data-testid'?: boolean | string;
}

export default function SelectSearchable({
  id,
  value,
  label,
  disabled,
  name,
  onChange,
  onCreate,
  isCreatable = false,
  options,
  required,
  placeholder = 'Selecione ou digite para buscar...',
  'data-testid': dataTestId,
}: SelectSearchableProps) {
  const theme = useTheme();

  const controlId = id ?? name;

  const testId = useMemo(() => {
    if (typeof dataTestId === 'string') return dataTestId;
    return dataTestId ? `input-selectsearch-${controlId}` : undefined;
  }, [dataTestId, controlId]);

  const SelectComponent = isCreatable ? CreatableSelect : ReactSelect;

  const selectedOption = options.find((opt) => opt.value === value) || null;

  const handleChange = (selectedOption: any) => {
    if (!onChange) return;

    const syntheticEvent = {
      target: {
        name,
        value: selectedOption?.value || '',
      },
    } as React.ChangeEvent<HTMLSelectElement>;

    onChange(syntheticEvent);
  };

  const customStyles: StylesConfig = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: '10px',
      border: `1px solid ${theme.colors.bordaInput}`,
      minHeight: '38px',
      boxShadow: state.isFocused
        ? `0 0 0 0.2rem ${theme.colors.azulgov}40`
        : 'none',
      '&:hover': {
        borderColor: theme.colors.azulgov,
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? theme.colors.azulgov
        : state.isFocused
          ? theme.colors.fundo
          : 'white',
      color: state.isSelected ? 'white' : theme.colors.textoComum,
      cursor: 'pointer',
      padding: '10px',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      maxHeight: '300px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '300px',
    }),
  };

  return (
    <FormGroup>
      <FormLabel
        style={{ color: theme.colors.textoAzul }}
        data-testid={testId ? `${testId}-label` : undefined}
      >
        {required ? (
          <>
            <span
              style={{ color: theme.colors.botaoConfirmar, fontWeight: 'bold' }}
            >
              {label}
            </span>
            <span style={{ color: theme.colors.vermelho, fontWeight: 'bold' }}>
              {' *'}
            </span>
          </>
        ) : (
          label
        )}
      </FormLabel>
      <div data-testid={testId}>
        <SelectComponent
          name={name}
          value={selectedOption}
          onChange={handleChange}
          onCreateOption={isCreatable ? onCreate : undefined}
          options={options}
          isDisabled={disabled}
          isClearable
          isSearchable
          placeholder={placeholder}
          styles={customStyles}
          noOptionsMessage={() => 'Nenhuma opção encontrada'}
          loadingMessage={() => 'Carregando...'}
          data-testid={dataTestId}
          filterOption={(opt, inputValue) =>
            opt.label.toLowerCase().includes(inputValue.toLowerCase())
          }
        />
      </div>
    </FormGroup>
  );
}
